import { batch, useComputed, useSignal, useSignalEffect, type ReadonlySignal, type Signal } from "@preact/signals";
import { Show } from "@preact/signals/utils";
import { useCallback } from "preact/hooks";
import { type Anchor, type System } from ".";
import { type LogData, type RangingStats, type Vec3 } from "../src/data";
import { projectDistance } from "./math";
import useTheme from "./theme";

const theme = useTheme();

function AxisSvg() {
	return (
		<>
			<line opacity={0.7} x1={-30} y1={0} x2={30} y2={0} stroke={theme.red} stroke-width="0.03" />
			<line opacity={0.7} x1={0} y1={-30} x2={0} y2={30} stroke={theme.green} stroke-width="0.03" />
			<circle cx={0} cy={0} r={0.05} fill={theme.subtext1} />
		</>
	);
}

function useProjectedStats(a: Anchor, pos: ReadonlySignal<Vec3 | undefined>, stats: ReadonlySignal<RangingStats | undefined>) {
	const min = useSignal<number>(0);
	const avg = useSignal<number>(0);
	const max = useSignal<number>(0);

	useSignalEffect(() => {
		batch(() => {
			if (stats.value == undefined || pos.value == undefined) {
				min.value = 0;
				avg.value = 0;
				max.value = 0;
				return;
			}

			min.value = projectDistance(stats.value.min, a.z, pos.value.z);
			avg.value = projectDistance(stats.value.avg, a.z, pos.value.z);
			max.value = projectDistance(stats.value.max, a.z, pos.value.z);
		});
	});

	return {min, max, avg};
}

function AnchorRingsLive({ a, isHover, color, systemPos }: { a: Anchor, isHover: ReadonlySignal<boolean>, color: ReadonlySignal<string>, systemPos: ReadonlySignal<Vec3> }) {
	const projDist = useComputed(() => projectDistance(a.distance.value, a.z, systemPos.value.z));

	return (
		<>
			<Show when={isHover}>
				<circle cx={0} cy={0} r={projDist} fill={color} opacity={0.5} />
			</Show>
			<circle cx={0} cy={0} r={projDist} stroke={a.stale.value ? `${theme.peach}44` : color} stroke-width="0.01" />
		</>
	);
}

function AnchorRingsLogged({ a, anchorData, isHover, color, center }: { a: Anchor, anchorData: ReadonlySignal<RangingStats | undefined>, isHover: ReadonlySignal<boolean>, color: ReadonlySignal<string>, center: ReadonlySignal<Vec3 | undefined> }) {
	const {min, avg, max} = useProjectedStats(a, center, anchorData);

	return (
		<>
			<Show when={isHover}>
				<circle cx={0} cy={0} r={avg} fill={color} opacity={0.5} />
			</Show>
			<circle cx={0} cy={0} r={min} stroke={color} stroke-width="0.01" />
			<circle cx={0} cy={0} r={avg} stroke={color} stroke-width="0.01" stroke-dasharray={0.2} />
			<circle cx={0} cy={0} r={max} stroke={color} stroke-width="0.01" />
		</>
	);
}

function AnchorRings({ a, isHover, data, systemPos }: { a: Anchor, isHover: ReadonlySignal<boolean>, data: ReadonlySignal<LogData | undefined>, systemPos: ReadonlySignal<Vec3> }) {
	const color = useComputed(() => isHover.value ? theme.sapphire : theme.teal);
	const anchorData = useComputed(() => data.value?.ranges[a.addr]);
	const isLogged = useComputed(() => data.value != undefined);
	const isStatic = useComputed(() => data.value?.type == "static");
	const loggedCenter = useComputed(() => data.value?.center);

	return (
		<Show when={a.visible}>
			<Show when={isLogged} fallback={<AnchorRingsLive a={a} isHover={isHover} color={color} systemPos={systemPos} />}>
				<Show when={isStatic}>
					<AnchorRingsLogged a={a} anchorData={anchorData} isHover={isHover} color={color} center={loggedCenter} />
				</Show>
			</Show>
		</Show>
	);
}

function AnchorSvg({ a, data, anchorHovered, systemPos }: { a: Anchor, data: ReadonlySignal<LogData | undefined>, anchorHovered: Signal<number>, systemPos: ReadonlySignal<Vec3> }) {
	const isHover = useComputed(() => anchorHovered.value == a.addr);

	const fill = useComputed(() => {
		if (isHover.value) {
			return a.visible.value ? theme.sapphire : theme.overlay1;
		} else {
			return a.visible.value ? theme.green : theme.subtext0;
		}
	});

	const onClick = useCallback((e: MouseEvent) => {
		if (e.ctrlKey) {
			// hide all others
		} else {
			// toggle self
			a.visible.value = !a.visible.peek();
		}
	}, []);

	return (
		<g transform={`translate(${a.x}, ${-a.y})`}>
			<AnchorRings a={a} isHover={isHover} data={data} systemPos={systemPos} />
			<g onMouseEnter={() => anchorHovered.value = a.addr} onMouseLeave={() => anchorHovered.value = NaN} onClick={onClick}>
				<rect x={-0.1} y={-0.1} height={0.2} width={0.4} fill="transparent"/>
				<rect x={-0.04} y={-0.04} height={0.08} width={0.08} fill={fill} />
				<text x={0.08} y={0.042} font-size={0.14} font-family="monospace" fill={fill}>A{a.addr}</text>
			</g>
		</g>
	);
}

function Anchors({ loggedData, system }: CommonProps) {
	return system.anchors.map((a) => <AnchorSvg a={a} data={loggedData} anchorHovered={system.anchorHovered} systemPos={system.pos} />);
}

function Center({loggedData}: {loggedData: ReadonlySignal<LogData | undefined>}) {
	const isLogged = useComputed(() => loggedData.value != undefined);
	const isRotate = useComputed(() => loggedData.value?.type == "rotate");
	const centerTrans = useComputed(() => {
		const c = loggedData.value?.center;
		if (c != undefined) {
			return `translate(${c.x}, ${-c.y})`;
		}
		return "";
	});

	return (
		<Show when={isLogged}>
			<g transform={centerTrans}>
				<circle cx={0} cy={0} r={0.01} fill={theme.blue} />

				<Show when={isRotate}>
					<circle cx={0} cy={0} r={0.95} stroke={theme.mauve} stroke-width="0.01" />
				</Show>
			</g>
		</Show>
	);
}

interface CommonProps {
	loggedData: ReadonlySignal<LogData | undefined>,
	system: System,
};

export function RenderSvgUnder({ loggedData, system }: CommonProps) {
	return (
		<>
			<AxisSvg />
			<Anchors loggedData={loggedData} system={system} />
			<Center loggedData={loggedData} />
		</>
	);
}

export function RenderSvgOver() {
	return null;
}
